import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, CheckCircle2 } from "lucide-react";
import { api, formatApiError, useAuth } from "@/context/AuthContext";
import { openBookingPayment } from "@/lib/razorpay";
import { loadPayPalScript } from "@/lib/paypal";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import StatusBadge from "@/components/StatusBadge";
import { SERVICES, serviceBySlug } from "@/data/services";

const PayPalBox = ({ bookingId, onPaid, onFailed }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: cfg } = await api.get("/payments/paypal/config");
        if (!cfg.configured) {
          setError("PayPal is not configured yet — choose Razorpay or pay at the desk.");
          return;
        }
        await loadPayPalScript(cfg.client_id, cfg.currency);
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        window.paypal
          .Buttons({
            createOrder: async () =>
              (await api.post("/payments/paypal/create-order", { booking_id: bookingId })).data
                .order_id,
            onApprove: async (data) => {
              try {
                await api.post("/payments/paypal/capture", {
                  booking_id: bookingId,
                  order_id: data.orderID,
                });
                toast.success("Payment confirmed via PayPal.");
                onPaid?.();
              } catch (err) {
                toast.error(formatApiError(err));
                onFailed?.();
              }
            },
            onCancel: () => toast("PayPal checkout cancelled — you can pay later from My Bookings."),
            onError: () => {
              toast.error("PayPal payment failed. No money was captured.");
              onFailed?.();
            },
          })
          .render(containerRef.current);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  return (
    <div className="mt-6 border-t border-slate-100 pt-6">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        Pay securely with PayPal
      </p>
      <div ref={containerRef} data-testid="paypal-buttons" className="mt-3" />
      {error && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="paypal-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default function Booking() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [form, setForm] = useState({
    service: params.get("service") || "",
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    notes: "",
    payment_method: "online",
  });
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const selected = serviceBySlug(form.service);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please select a service first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/bookings", form);
      const submitted = { ...form };
      const svc = serviceBySlug(form.service);
      let paymentStatus = data.payment_required ? "unpaid" : "not_required";

      if (data.payment_required && form.payment_method === "desk") {
        toast.success("Booking received — pay the fee at the service desk when you visit.");
      } else if (data.payment_required && form.payment_method === "paypal") {
        toast.success("Booking received — complete your payment with PayPal below.");
      } else if (data.payment_required && form.payment_method === "stripe") {
        try {
          const { data: stripeSession } = await api.post("/payments/stripe/checkout", {
            booking_id: data.booking_id,
            origin_url: window.location.origin,
          });
          toast.success("Redirecting to secure Stripe checkout…");
          window.location.href = stripeSession.checkout_url;
          return;
        } catch (err) {
          toast.error(formatApiError(err));
        }
      } else if (data.payment_required) {
        try {
          const { data: cfg } = await api.get("/payments/config");
          if (cfg.configured) {
            toast.success(`Booking received — pay the ₹${data.fee_paise / 100} fee to lock your slot.`);
            await openBookingPayment({
              bookingId: data.booking_id,
              details: submitted,
              onPaid: () => setConfirmation((c) => (c ? { ...c, paymentStatus: "paid" } : c)),
              onFailed: () => setConfirmation((c) => (c ? { ...c, paymentStatus: "failed" } : c)),
            });
          } else {
            toast.success("Booking received — online payment is being set up; pay at the service desk.");
          }
        } catch {
          toast.success("Booking received — fee payable at the service desk.");
        }
      } else {
        toast.success("Booking confirmed — this service is free of charge.");
      }

      setConfirmation({
        bookingId: data.booking_id,
        bookingRef: data.booking_ref,
        service: svc?.title || submitted.service,
        date: submitted.preferred_date || "To be scheduled",
        status: "pending",
        paymentStatus,
        paymentMethod: form.payment_method,
        amount: data.fee_paise / 100,
        name: submitted.name,
        email: submitted.email,
        phone: submitted.phone,
      });
      setForm({ service: "", name: user?.name || "", email: user?.email || "", phone: "", preferred_date: "", notes: "", payment_method: "online" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Kicker>Book a Service</Kicker>
          <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-4xl sm:text-5xl">
              Book your slot
            </MaskedLine>
          </h1>
          <Reveal delay={0.25} className="mt-4 max-w-2xl">
            <p className="text-base leading-relaxed text-slate-700">
              Choose a service, share your details and preferred date. Fees, where applicable, are
              shown before you confirm.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        {confirmation ? (
          <Reveal>
            <div className="rounded-xl border border-green-200 bg-white p-8 shadow-sm" data-testid="booking-confirmation">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" aria-hidden="true" />
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  Booking received
                </h2>
              </div>
              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Booking ID</dt>
                  <dd className="font-mono font-medium text-slate-900" data-testid="confirm-booking-id">
                    {confirmation.bookingRef}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Service</dt>
                  <dd className="font-medium text-slate-900">{confirmation.service}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Booking date</dt>
                  <dd className="font-medium text-slate-900">{confirmation.date}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Booking status</dt>
                  <dd><StatusBadge value={confirmation.status} testId="confirm-status" /></dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Payment status</dt>
                  <dd>
                    <StatusBadge
                      value={
                        confirmation.paymentStatus === "unpaid" && confirmation.paymentMethod === "desk"
                          ? "pay_at_desk"
                          : confirmation.paymentStatus
                      }
                      testId="confirm-payment-status"
                    />
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Amount</dt>
                  <dd className="font-medium text-slate-900">
                    {confirmation.amount > 0 ? `₹${confirmation.amount}` : "Free"}
                  </dd>
                </div>
              </dl>
              <div className="mt-8 flex flex-wrap gap-3">
                {confirmation.amount > 0 &&
                  ["unpaid", "pending_payment", "failed"].includes(confirmation.paymentStatus) &&
                  confirmation.paymentMethod === "online" && (
                    <button
                      onClick={() =>
                        openBookingPayment({
                          bookingId: confirmation.bookingId,
                          details: {
                            name: confirmation.name,
                            email: confirmation.email,
                            phone: confirmation.phone,
                          },
                          onPaid: () =>
                            setConfirmation((c) => (c ? { ...c, paymentStatus: "paid" } : c)),
                          onFailed: () =>
                            setConfirmation((c) => (c ? { ...c, paymentStatus: "failed" } : c)),
                        })
                      }
                      data-testid="confirm-pay-now-button"
                      className="inline-flex items-center gap-2 rounded-md bg-green-600 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-green-700"
                    >
                      Pay Now — ₹{confirmation.amount}
                    </button>
                  )}
                {confirmation.paymentStatus === "paid" && (
                  <Link
                    to={`/receipt/${confirmation.bookingId}`}
                    data-testid="confirm-receipt-link"
                    className="inline-flex items-center gap-2 rounded-md border-2 border-green-300 px-5 py-2.5 text-sm font-medium text-green-700 transition-colors duration-200 hover:border-green-500"
                  >
                    View receipt
                  </Link>
                )}
                <Link
                  to="/my-bookings"
                  data-testid="confirm-my-bookings-link"
                  className="inline-flex items-center gap-2 rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
                >
                  View My Bookings
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <button
                  onClick={() => setConfirmation(null)}
                  data-testid="confirm-book-another-button"
                  className="rounded-md border-2 border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-blue-900 hover:text-blue-900"
                >
                  Book another service
                </button>
              </div>
              {confirmation.paymentMethod === "paypal" &&
                confirmation.paymentStatus !== "paid" &&
                confirmation.amount > 0 && (
                  <PayPalBox
                    bookingId={confirmation.bookingId}
                    onPaid={() =>
                      setConfirmation((c) => (c ? { ...c, paymentStatus: "paid" } : c))
                    }
                    onFailed={() =>
                      setConfirmation((c) => (c ? { ...c, paymentStatus: "failed" } : c))
                    }
                  />
                )}
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10"
            >
              <div>
                <label htmlFor="booking-service" className="field-label">
                  Select service
                </label>
                <select
                  id="booking-service"
                  data-testid="booking-service-select"
                  className="field-input"
                  value={form.service}
                  onChange={set("service")}
                  required
                >
                  <option value="">Choose a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.title} {s.fee > 0 ? `(₹${s.fee})` : "(Free)"}
                    </option>
                  ))}
                </select>
                {selected && (
                  <p className="mt-2 text-sm text-slate-600">
                    {selected.tagline} —{" "}
                    <Link to={`/services/${selected.slug}`} className="font-medium text-blue-900 hover:underline">
                      view details
                    </Link>
                  </p>
                )}
              </div>

              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="booking-name" className="field-label">Full name</label>
                  <input
                    id="booking-name"
                    data-testid="booking-name-input"
                    className="field-input"
                    value={form.name}
                    onChange={set("name")}
                    required
                    minLength={2}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="booking-email" className="field-label">Email</label>
                  <input
                    id="booking-email"
                    type="email"
                    data-testid="booking-email-input"
                    className="field-input"
                    value={form.email}
                    onChange={set("email")}
                    required
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="booking-phone" className="field-label">Phone (optional)</label>
                  <input
                    id="booking-phone"
                    data-testid="booking-phone-input"
                    className="field-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Mobile number"
                  />
                </div>
                <div>
                  <label htmlFor="booking-date" className="field-label">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                      Preferred date
                    </span>
                  </label>
                  <input
                    id="booking-date"
                    type="date"
                    data-testid="booking-date-input"
                    className="field-input"
                    value={form.preferred_date}
                    onChange={set("preferred_date")}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="booking-notes" className="field-label">
                  Tell us what you need help with
                </label>
                <textarea
                  id="booking-notes"
                  data-testid="booking-notes-input"
                  className="field-input min-h-28 resize-y"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="e.g. I need help applying for the PM-KISAN scheme…"
                />
              </div>

              {selected && selected.fee > 0 && (
                <fieldset className="mt-6" data-testid="booking-payment-method">
                  <legend className="field-label">Payment method — fee ₹{selected.fee}</legend>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-200 ${
                        form.payment_method === "online"
                          ? "border-blue-900 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="online"
                        checked={form.payment_method === "online"}
                        onChange={set("payment_method")}
                        data-testid="booking-pay-online-radio"
                        className="mt-1 h-4 w-4 accent-blue-900"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">Razorpay</span>
                        <span className="mt-0.5 block text-xs text-slate-600">
                          UPI, cards, netbanking
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-200 ${
                        form.payment_method === "paypal"
                          ? "border-blue-900 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="paypal"
                        checked={form.payment_method === "paypal"}
                        onChange={set("payment_method")}
                        data-testid="booking-pay-paypal-radio"
                        className="mt-1 h-4 w-4 accent-blue-900"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">PayPal</span>
                        <span className="mt-0.5 block text-xs text-slate-600">
                          International cards & PayPal balance
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-200 ${
                        form.payment_method === "stripe"
                          ? "border-blue-900 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe"
                        checked={form.payment_method === "stripe"}
                        onChange={set("payment_method")}
                        data-testid="booking-pay-stripe-radio"
                        className="mt-1 h-4 w-4 accent-blue-900"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">Stripe</span>
                        <span className="mt-0.5 block text-xs text-slate-600">
                          Secure card checkout (test mode)
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-200 ${
                        form.payment_method === "desk"
                          ? "border-blue-900 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="desk"
                        checked={form.payment_method === "desk"}
                        onChange={set("payment_method")}
                        data-testid="booking-pay-desk-radio"
                        className="mt-1 h-4 w-4 accent-blue-900"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">Pay at the service desk</span>
                        <span className="mt-0.5 block text-xs text-slate-600">
                          Book now, pay in person — admin confirms later
                        </span>
                      </span>
                    </label>
                  </div>
                </fieldset>
              )}

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {sending ? "Submitting…" : "Confirm booking"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                {selected && selected.fee > 0
                  ? `Fee ₹${selected.fee} — online via Razorpay or at the desk`
                  : "Free services need no payment"}
              </p>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
